import { createContext, useContext, useEffect, useReducer, useState } from 'react';
import { useWebView } from '@myrealtrip/device-detect';

import { TokenRefreshResponse, TokenWebViewResponse } from '~models/TokenDto';
import useTokenQueryForWeb from '~queries/token/useTokenQueryForWeb';
import useTokenQueryForWebView from '~queries/token/useTokenQueryForWebView';

const initialState: State = {
  isLoggedIn: false,
};

export const AuthContext = createContext<State>(initialState);
export const AuthDispatchContext = createContext<React.Dispatch<Action>>(() => null);

export const useAuthContext = () => useContext(AuthContext);
export const useAuthDispatchContext = () => useContext(AuthDispatchContext);

type State = {
  isLoggedIn: boolean;
};

export enum ActionType {
  INITIALIZE = 'INITIALIZE',
  LOGOUT = 'LOGOUT',
  UPDATE = 'UPDATE',
}

type InitializeAction = {
  type: ActionType.INITIALIZE;
  payload: { token: string | null };
};
type UpdateAction = {
  type: ActionType.UPDATE;
  payload: { token: string | null };
};
type LogoutAction = {
  type: ActionType.LOGOUT;
};

type Action = InitializeAction | UpdateAction | LogoutAction;

type Handler = (state: State, action: Action) => State;

const handlers: Record<ActionType, Handler> = {
  INITIALIZE: (state: State, action: Action) => {
    const { token } = (action as InitializeAction).payload;

    if (!token) {
      return { ...state, isLoggedIn: false };
    }

    return { ...state, isLoggedIn: true };
  },
  UPDATE: (state: State, action: Action) => {
    const { token } = (action as UpdateAction).payload;

    if (!token) {
      return { ...state, isLoggedIn: false };
    }

    return { ...state, isLoggedIn: true };
  },
  LOGOUT: (state: State) => ({ ...state, isLoggedIn: false }),
};

const reducer = (state: State, action: Action): State =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

export function AuthProvider({ children }: React.PropsWithChildren) {
  const { isWebView } = useWebView();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [isInitialized, setIsInitialized] = useState(false);

  const handleSuccess = (response: TokenRefreshResponse | TokenWebViewResponse) => {
    dispatch({
      type: ActionType.INITIALIZE,
      payload: { token: response.data?.apiAccessToken ?? null },
    });
  };
  const handleError = () => {
    dispatch({
      type: ActionType.INITIALIZE,
      payload: { token: null },
    });
  };
  const { isSuccess: isWebSuccess, isError: isWebError, data: webData } = useTokenQueryForWeb();
  const {
    isSuccess: isWebViewSuccess,
    isError: isWebViewError,
    data: webviewData,
  } = useTokenQueryForWebView();

  useEffect(() => {
    if (isInitialized) {
      return;
    }

    if (isWebView && isWebViewSuccess) {
      handleSuccess(webviewData);
      setIsInitialized(true);
      return;
    }

    if (!isWebView && isWebSuccess) {
      handleSuccess(webData);
      setIsInitialized(true);
      return;
    }
  }, [isWebSuccess, isWebView, isWebViewSuccess, webData, webviewData, isInitialized]);

  useEffect(() => {
    if (isWebError || isWebViewError) {
      handleError();
    }
  }, [isWebError, isWebViewError]);

  return (
    <AuthContext.Provider value={state}>
      <AuthDispatchContext.Provider value={dispatch}>{children}</AuthDispatchContext.Provider>
    </AuthContext.Provider>
  );
}
